/**
 * Money is always handled as an integer number of pence (GBP minor units).
 * Never use floating point pounds for arithmetic.
 */
export type Pence = number;

const MONEY_RE = /^([+-])?£?\s*(\d{1,3}(,\d{3})+|\d+)?(\.(\d{1,2}))?$/;

/** Parses a pounds string such as "£1,234.56", "12", "-3.5" into pence. Returns null if invalid. */
export function parsePounds(input: string | number): Pence | null {
  if (typeof input === "number") {
    if (!Number.isFinite(input)) return null;
    return Math.round(input * 100);
  }
  const trimmed = input.trim().replace(/^£\s*-/, "-£");
  if (trimmed === "") return null;
  const match = MONEY_RE.exec(trimmed);
  if (!match || (match[2] === undefined && match[5] === undefined)) return null;
  const whole = Number((match[2] ?? "0").replaceAll(",", ""));
  const fraction = Number((match[5] ?? "0").padEnd(2, "0"));
  const pence = whole * 100 + fraction;
  if (!Number.isSafeInteger(pence)) return null;
  return match[1] === "-" ? -pence : pence;
}

/** "1234.56" — plain decimal string, suitable for inputs and CSV. */
export function penceToDecimal(pence: Pence): string {
  const sign = pence < 0 ? "-" : "";
  const abs = Math.abs(pence);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

const gbp = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });
const gbpWhole = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

/** "£1,234.56" (or "£1,235" when whole=true). */
export function formatPence(pence: Pence, opts: { whole?: boolean } = {}): string {
  return (opts.whole ? gbpWhole : gbp).format(pence / 100);
}

export function sumPence(values: Iterable<Pence>): Pence {
  let total = 0;
  for (const v of values) total += v;
  return total;
}

export function isPence(value: unknown): value is Pence {
  return typeof value === "number" && Number.isSafeInteger(value);
}
